/**
 * Module dependencies.
 */

import isString from 'lodash/isString';
import { ReactNode, forwardRef } from 'react';
import { ErrorMessage, FloatingLabelStyle } from 'src/components/core/forms/styles';
import { Text } from 'src/components/core/text';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';

/**
 * `Option` type.
 */

type Option = {
  description?: ReactNode;
  label: ReactNode;
  value: string | undefined;
};

/**
 * `Fieldset` styled component.
 */

const Fieldset = styled.div`
  border: none;
  padding: 0 16px;
`;

/**
 * Export `CheckboxProps` type.
 */

export type RadioGroupProps = {
  error?: string;
  label?: ReactNode;
  options: Option[];
} & Omit<React.ComponentProps<'input'>, 'as' | 'type'>;

/**
 * `RadioWrapper` styled component.
 */

const RadioWrapper = styled.span`
  background-color: var(--radio-background-color);
  border: var(--radio-border-width) solid var(--radio-border-color);
  border-radius: 50%;
  grid-area: radio;
  height: var(--radio-size);
  position: relative;
  transition: var(--transition-default);
  transition-property: background-color, border;
  width: var(--radio-size);
`;

/**
 * `LabelWrapper` styled component.
 */

const LabelWrapper = styled.label<Pick<RadioGroupProps, 'disabled'>>`
  --radio-background-color: var(--color-grey200);
  --radio-border-color: var(--color-grey500);
  --radio-border-width: 1px;
  --radio-icon-color: transparent;
  --radio-label-color: var(--text-color);

  align-items: flex-start;
  cursor: pointer;
  display: grid;
  grid-column-gap: var(--radio-column-gap);
  grid-template-areas: 'radio label';
  grid-template-columns: var(--radio-size) 1fr;
  padding: 8px 0;
  position: relative;
  width: 100%;

  &:focus,
  &:focus-within,
  &:hover {
    --radio-background-color: var(--color-grey300);
    --radio-border-color: var(--color-grey500);
    --radio-border-width: 3px;

    input:checked ~ ${RadioWrapper} {
      --radio-background-color: var(--color-white);
      --radio-border-color: var(--color-blue700);
      --radio-border-width: 5px;
    }
  }

  &:active {
    --radio-background-color: var(--color-grey400);
    --radio-border-color: var(--color-grey600);
    --radio-border-width: 3px;
  }

  input:checked {
    ~ ${RadioWrapper} {
      --radio-background-color: var(--color-white);
      --radio-border-color: var(--color-blue500);
      --radio-border-width: 6px;
    }

    &[disabled] ~ ${RadioWrapper} {
      --radio-background-color: var(--color-white);
      --radio-border-color: var(--color-blue300);
      --radio-border-width: 6px;
    }
  }

  ${ifProp(
    'disabled',
    css`
      --radio-background-color: var(--color-grey200);
      --radio-border-color: var(--color-grey200);

      color: var(--color-grey700);
      cursor: default;
      pointer-events: none;
    `
  )}
`;

/**
 * `Input` styled component.
 */

const Input = styled.input<Pick<RadioGroupProps, 'disabled'>>`
  cursor: pointer;
  grid-area: radio;
  height: var(--radio-size);
  opacity: 0;
  width: var(--radio-size);
  z-index: 1;
`;

/**
 * `Label` styled component.
 */

const Label = styled(Text).attrs({
  variant: 'paragraph2'
})<Pick<RadioGroupProps, 'disabled'>>`
  grid-area: label;
  overflow-wrap: anywhere;
  transition: color var(--transition-default);
`;

/**
 * `FieldLabel` styled component.
 */

const FieldLabel = styled(FloatingLabelStyle)`
  display: block;
  padding-bottom: 8px;
  padding-left: 0;
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text).attrs({
  fontWeight: 400,
  variant: 'paragraph2'
})`
  display: block;
`;

/**
 * `RadioGroup` styled component.
 */

export const RadioGroup = forwardRef((props: RadioGroupProps, ref: any) => {
  const { className, disabled, error, label, name, options, value: inputValue, ...rest } = props;

  return (
    <Fieldset className={className}>
      {!isString(label) ? label : <FieldLabel>{label}</FieldLabel>}

      <ul>
        {options.map(({ description, label, value }) => (
          <li key={`${name}-${value}`}>
            <LabelWrapper disabled={disabled}>
              <Input
                checked={inputValue === value}
                disabled={disabled}
                id={value}
                name={name}
                ref={ref}
                type={'radio'}
                value={value}
                {...rest}
              />

              <Label>
                {label}

                {description && <Description>{description}</Description>}
              </Label>

              <RadioWrapper />
            </LabelWrapper>
          </li>
        ))}
      </ul>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Fieldset>
  );
});

/**
 * `RadioGroup` display name.
 */

RadioGroup.displayName = 'RadioGroup';
